import {
  Button,
  Classes,
  Dialog,
  FormGroup,
  InputGroup,
} from '@blueprintjs/core';
import { Businesses } from '@just-in-types';
import InputMask from 'imask';
import * as React from 'react';
import { connect } from 'react-redux';
import { IState } from '../store';
import { addNewBusiness, AddNewBusiness } from '../store/actions';
import { convertToPath } from '../views/Businesses';
import CopyLink from './CopyLink';

export const defaultTheme = {
  background: 'linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.8))',
  colors: {
    error: '#e23229',
    primary: '#2b3c8b',
    secondary: '#fcad14',
    success: '#467f1e',
    warning: '#e8a530',
  },
  logo: '',
};

interface IBusinessAdderDispatchProps {
  onAddNewBusiness: AddNewBusiness;
}

interface IBusinessAdderOwnProps {
  onToggleShow: () => void;
  show: boolean;
  sites?: Businesses.Site[];
}

interface IBusinessAdderStateProps {
  businesses: IState['businesses']['businesses'];
}

type BusinessAdderProps = IBusinessAdderDispatchProps &
  IBusinessAdderOwnProps &
  IBusinessAdderStateProps;

interface IBusinessAdderState {
  adding: boolean;
  businessName: string;
  businessNameTouched: boolean;
  businessNameValid: boolean;
  guid: string;
  guidTouched: boolean;
  guidValid: boolean;
  keyword: string;
  keywordTouched: boolean;
  keywordValid: boolean;
  path: string;
  shortcode: string;
  shortcodeTouched: boolean;
  shortcodeValid: boolean;
}

class BusinessAdder extends React.Component<
  BusinessAdderProps,
  IBusinessAdderState
> {
  private static theme = defaultTheme;

  public guidInterval: number;
  public guidInput: null | HTMLInputElement = null;
  public guidInputMask: null | any = null;

  public state: IBusinessAdderState = {
    adding: false,
    businessName: '',
    businessNameTouched: false,
    businessNameValid: false,
    guid: '',
    guidTouched: false,
    guidValid: false,
    keyword: '',
    keywordTouched: false,
    keywordValid: false,
    path: '',
    shortcode: '',
    shortcodeTouched: false,
    shortcodeValid: false,
  };

  public componentDidMount() {
    this.initInputMask();
  }

  public componentWillUnmount() {
    if (this.guidInterval) {
      window.clearInterval(this.guidInterval);
    }
  }

  public render() {
    const {
      adding,
      businessName,
      businessNameTouched,
      businessNameValid,
      guidTouched,
      guidValid,
      keyword,
      keywordTouched,
      keywordValid,
      path,
      shortcode,
      shortcodeTouched,
      shortcodeValid,
    } = this.state;
    const { onToggleShow, show } = this.props;
    const businessNameIntent = this.getIntent(
      businessNameTouched,
      businessNameValid,
    );
    const guidIntent = this.getIntent(guidTouched, guidValid);
    const keywordIntent = this.getIntent(keywordTouched, keywordValid);
    const shortcodeIntent = this.getIntent(shortcodeTouched, shortcodeValid);
    const addButtonDisabled =
      !businessNameValid || !guidValid || !keywordValid || !shortcodeValid;

    return (
      <Dialog isOpen={show} onClose={onToggleShow} title="add new business">
        <div className={Classes.DIALOG_BODY}>
          <FormGroup label="business name">
            <InputGroup
              id="business-name"
              intent={businessNameIntent}
              name="businessName"
              onBlur={this.setTouched}
              onChange={this.handleInput}
              type="text"
              value={businessName}
            />
          </FormGroup>
          <FormGroup label="ivision guid" labelFor="guid">
            <InputGroup
              id="guid"
              intent={guidIntent}
              inputRef={ref => (this.guidInput = ref)}
              onBlur={this.setTouched}
              onChange={this.handleInput}
              onInput={this.handleInput}
              name="guid"
              style={{ fontFamily: 'monospace' }}
              type="text"
            />
          </FormGroup>
          <FormGroup
            label="ivision keyword"
            labelInfo="( optional )"
            labelFor="keyword">
            <InputGroup
              id="keyword"
              intent={keywordIntent}
              onBlur={this.setTouched}
              onChange={this.handleInput}
              name="keyword"
              type="text"
              value={keyword}
            />
          </FormGroup>
          <FormGroup label="ivision code" labelFor="shortcode">
            <InputGroup
              id="shortcode"
              intent={shortcodeIntent}
              onBlur={this.setTouched}
              onChange={this.handleInput}
              name="shortcode"
              type="text"
              value={shortcode}
            />
          </FormGroup>
          <CopyLink url={`https://kiosk.texnrewards.com/${path}`} />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button
              disabled={addButtonDisabled}
              intent="success"
              loading={adding}
              onClick={this.addBusiness}>
              add business
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  private initInputMask = () => {
    this.guidInterval = window.setInterval(() => {
      if (this.guidInput) {
        this.setInputMask();
        window.clearInterval(this.guidInterval);
      }
    });
  };

  private setInputMask = () => {
    this.guidInputMask = new InputMask(this.guidInput, {
      definitions: {
        '@': /[a-zA-Z0-9]/,
      },
      lazy: false,
      mask: '\\{@@@@@@@@-@@@@-@@@@-@@@@-@@@@@@@@@@@@\\}',
      placeholderChar: ' ',
    });
  };

  private handleInput = ({
    currentTarget: { name, value },
  }: React.FormEvent<HTMLInputElement>) => {
    if (name === 'businessName') {
      this.handleBusinessName(value);
    }
    if (name === 'guid') {
      this.handleGuid(value);
    }
    if (name === 'keyword') {
      this.handleKeyword(value);
    }
    if (name === 'shortcode') {
      this.handleShortcode(value);
    }
  };

  private handleBusinessName = (businessName: string) =>
    this.setState(state => {
      const path = convertToPath(businessName);

      return {
        ...state,
        businessName,
        businessNameValid: this.isValidBusinessName(businessName, path),
        path,
      };
    });

  private handleGuid = (guid: string) => {
    this.setState(state => ({
      ...state,
      guid,
      guidValid: this.isValidGuid(guid),
    }));
  };

  private handleKeyword = (keyword: string) =>
    this.setState(state => ({
      ...state,
      keyword,
      keywordValid: this.isValidKeyword(keyword),
    }));

  private handleShortcode = (shortcode: string) =>
    this.setState(state => {
      const clean = shortcode.replace(/\D/g, '');
      const shortened = clean.substring(0, 11);

      return {
        ...state,
        shortcode: shortened,
        shortcodeValid: this.isValidShortcode(shortened),
      };
    });

  private isValidBusinessName = (newBusinessName: string, newPath: string) =>
    newBusinessName.length > 0 &&
    this.props.businesses.every(
      ({ businessName, path }) =>
        newBusinessName !== businessName && newPath !== path,
    )
      ? true
      : false;

  private isValidGuid = (guid: string) =>
    guid.match(/{\w{8}-\w{4}-\w{4}-\w{4}-\w{12}}/g) ? true : false;

  private isValidKeyword = (keyword: string) =>
    (keyword.match(/^\w+$/g) ? true : false) || keyword === '';

  private isValidShortcode = (shortcode: string) => {
    const isValidShortcode1 = shortcode.match(/^\d{5}$/g) ? true : false;
    const isValidShortcode2 = shortcode.match(/^\d{11}$/g) ? true : false;
    if(isValidShortcode1 || isValidShortcode2){
      return true;
    }else{
      return false;
    }
  }
    

  private setTouched = ({
    currentTarget: { name },
  }: React.FocusEvent<HTMLInputElement>) => {
    if (name === 'businessName') {
      this.setState(({ businessName, path }) => ({
        businessNameTouched: true,
        businessNameValid: this.isValidBusinessName(businessName, path),
      }));
    }
    if (name === 'guid') {
      this.setState(({ guid }) => ({
        guidTouched: true,
        guidValid: this.isValidGuid(guid),
      }));
    }
    if (name === 'keyword') {
      this.setState(({ keyword }) => ({
        keywordTouched: true,
        keywordValid: this.isValidKeyword(keyword),
      }));
    }
    if (name === 'shortcode') {
      this.setState(({ shortcode }) => ({
        shortcodeTouched: true,
        shortcodeValid: this.isValidShortcode(shortcode),
      }));
    }
  };

  private getIntent = (touched: boolean, valid: boolean) =>
    valid ? 'success' : touched ? 'danger' : 'none';

  private addBusiness = async () => {
    const { onAddNewBusiness, onToggleShow } = this.props;
    const { businessName, guid, keyword, path, shortcode } = this.state;

    this.setState({ adding: true });

    const business = {
      businessName,
      guid,
      keyword,
      path,
      shortcode,
      theme: BusinessAdder.theme,
    };

    await onAddNewBusiness(business);

    this.setState({ adding: false });

    onToggleShow();
  };
}

const mapStateToProps = ({ businesses: { businesses } }: IState) => ({
  businesses,
});

const mapDispatchToProps = {
  onAddNewBusiness: addNewBusiness,
};

export default connect<
  IBusinessAdderStateProps,
  IBusinessAdderDispatchProps,
  IBusinessAdderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(BusinessAdder);



// WEBPACK FOOTER //
// ./src/components/BusinessAdder.tsx